import { Checkbox, EmptyState, InfoIcon } from '@bp/ui-components';
import classNames from 'classnames';
import { FieldArray, Form, Formik, FormikHelpers } from 'formik';
import { useTranslation } from 'react-i18next';
import { useLoadBasicData } from '../../../hooks/useLoadBasicData';
import { useMemorizedCacheTag } from '../../../hooks/useMemorizedCacheTag';
import { usePreviousSchoolYear, useSelectedSchoolYear } from '../../../hooks/useSelectedSchoolYear';
import {
  AdditionalTeachingLoadInput,
  use_ClassesQuery,
  useAdditionalTeachingLoadsListQuery,
  useCreateAdditionalTeachingLoadMutation,
} from '../../../types/planung-graphql-client-defs';
import { showSuccessToast, showUserErrorToast } from '../../../utils/toast';
import { ModalBottomButtons } from '../../ModalBottomButtons/ModalBottomButtons';
import { AdditionalDeputateTableType } from '../tables/AdditionalDeputateTable';
import styles from './InheritDeputateForm.module.scss';
import { InheritDeputateFormRow } from './InheritDeputateFormRow';

type InheritDeputateFormProps = {
  onLoad: (isLoading: boolean) => void;
  onClose: () => void;
};

export type InheritDeputateFormType = (AdditionalDeputateTableType & {
  hadLastYearTeacher: boolean;
  hadLastYearSubject: boolean;
  hadLastYearClass: boolean;
  selected: boolean;
})[];

export const InheritDeputateForm = ({ onLoad, onClose }: InheritDeputateFormProps) => {
  const { t } = useTranslation();
  const currentSchoolYear = useSelectedSchoolYear();
  const previousSchoolYear = usePreviousSchoolYear();
  const classContext = useMemorizedCacheTag('CLASSES');
  const deputateContext = useMemorizedCacheTag('ADDITIONAL_TEACHINGLOAD');

  const [, createAdditionalTeachingLoadForm] = useCreateAdditionalTeachingLoadMutation();

  const [{ data: lastYearDeputateData }] = useAdditionalTeachingLoadsListQuery({
    variables: { schoolyear: previousSchoolYear?.uuid ?? '' },
    context: deputateContext,
    pause: !previousSchoolYear,
  });

  const [{ data: lastYearClassesData }] = use_ClassesQuery({
    variables: { schoolYearUuid: previousSchoolYear?.uuid },
    pause: !previousSchoolYear,
    context: classContext,
  });

  const [{ data: thisYearClassesData }] = use_ClassesQuery({
    variables: { schoolYearUuid: currentSchoolYear?.uuid },
    pause: !previousSchoolYear,
    context: classContext,
  });

  const { teacherData, subjectData } = useLoadBasicData({ pause: !previousSchoolYear });

  const lastYearDeputates: InheritDeputateFormType =
    lastYearDeputateData?.additionalTeachingLoads.map((teachingLoad) => {
      const newTeacher = teacherData?.people.find((t) => t.uuid === teachingLoad.teacher?.uuid && t.active);
      const newSubject = subjectData?.subjects.find((s) => s.uuid === teachingLoad.subject?.uuid && s.active);

      const oldGrade = lastYearClassesData?.classes.find((c) => c.uuid === teachingLoad.class?.uuid)?.grade;
      const oldGradeGroup = lastYearClassesData?.classes.find((c) => c.uuid === teachingLoad.class?.uuid)?.gradeGroup;
      const newClass = thisYearClassesData?.classes.find((c) => {
        return c.grade === oldGrade && c.gradeGroup?.uuid === oldGradeGroup?.uuid;
      });

      return {
        uuid: teachingLoad.uuid,
        teacherUuid: newTeacher?.uuid ?? '',
        teacherName: newTeacher?.listName ?? '',
        hadLastYearTeacher: !!teachingLoad.teacher,
        subjectUuid: newSubject?.uuid ?? '',
        subjectName: newSubject?.name ?? '',
        hadLastYearSubject: !!teachingLoad.subject,
        classUuid: newClass?.uuid ?? '',
        className: newClass?.name ?? '',
        hadLastYearClass: !!teachingLoad.class,
        hours: teachingLoad.hours.toString() ?? '',
        hoursRaw: teachingLoad.hoursRaw?.toString() ?? '',
        comment: teachingLoad.comment ?? '',
        selected: false,
      };
    }) ?? [];

  async function handleSubmit(values: InheritDeputateFormType, helper: FormikHelpers<InheritDeputateFormType>) {
    onLoad(true);

    const deputateToImport = values.filter((d) => d.selected);

    let hasError = false;
    deputateToImport.forEach(({ uuid, teacherUuid }) => {
      if (!teacherUuid || teacherUuid === '') {
        const index = lastYearDeputates.findIndex((last) => last.uuid === uuid);
        helper.setFieldError(`${index}.teacherUuid`, 'error');
        hasError = true;
      }
    });

    if (hasError) {
      onLoad(false);
      return;
    }

    let inheritCount = 0;

    for (const deputate of deputateToImport) {
      const input: AdditionalTeachingLoadInput = {
        schoolYearUuid: currentSchoolYear?.uuid ?? '',
        teacherUuid: deputate.teacherUuid,
        subjectUuid: deputate.subjectUuid || null,
        classUuid: deputate.classUuid || null,
        hours: parseFloat(deputate.hours),
        hoursRaw: deputate.hoursRaw ?? deputate.hours,
        comment: deputate.comment || null,
      };

      const result = await createAdditionalTeachingLoadForm({ input }, deputateContext);

      if (result.error) {
        showUserErrorToast({ error: result.error });
        break;
      } else {
        inheritCount++;
      }
    }

    if (inheritCount > 0) {
      showSuccessToast(t('additionalTeachingLoads.inheritSuccess', { count: inheritCount }));
    }

    onLoad(false);
    helper.resetForm();
    onClose();
  }

  return !previousSchoolYear || lastYearDeputates.length === 0 ? (
    <EmptyState
      title={!previousSchoolYear ? t('common.noPreviousYear') : t('additionalTeachingLoads.noPreviousTeachingLoads')}
      icon={<InfoIcon />}
    />
  ) : (
    <Formik<InheritDeputateFormType> initialValues={lastYearDeputates} onSubmit={handleSubmit}>
      {({ values, isSubmitting, resetForm, setValues }) => {
        return (
          <Form className={styles['inherit-deputate-form']}>
            <div className={styles.header}>
              <div className={styles.checkbox}>
                <Checkbox
                  name='select-all'
                  checked={values.filter((d) => d.selected).length === values.length}
                  onChange={async () => {
                    let selected = false;
                    if (values.filter((v) => v.selected).length === values.length) {
                      selected = false;
                    } else {
                      selected = true;
                    }
                    await setValues((values) => {
                      return values.map((v) => {
                        return { ...v, selected };
                      });
                    });
                  }}
                />
              </div>
              <div className={styles.large}>{t('persons.title', { context: 'neutral', count: 1 })}</div>
              <div className={styles.large}>{t('subject.name')}</div>
              <div className={styles.large}>{t('classes.title', { count: 1 })}</div>
              <div className={styles.small}>{t('common.hours')}</div>
              <div className={styles.medium}>{t('common.comment')}</div>
            </div>

            <div className={classNames(styles.body, 'has-scrollbar')}>
              <FieldArray
                name='deputate'
                render={(arrayHelpers) => {
                  return values.map((deputat, index) => (
                    <InheritDeputateFormRow
                      key={deputat.uuid}
                      index={index}
                      originalTeacher={
                        !!lastYearDeputates[index].teacherUuid && lastYearDeputates[index].teacherUuid !== ''
                      }
                      originalSubject={
                        !!lastYearDeputates[index].subjectUuid && lastYearDeputates[index].subjectUuid !== ''
                      }
                      originalClass={!!lastYearDeputates[index].classUuid && lastYearDeputates[index].classUuid !== ''}
                      helper={arrayHelpers}
                    />
                  ));
                }}
              />
            </div>

            <ModalBottomButtons
              closeButton={{
                callback: () => {
                  resetForm();
                  onClose();
                },
              }}
              submitButton={{ disabled: isSubmitting || values.filter((d) => d.selected).length === 0 }}
              className='px-6'
            />
          </Form>
        );
      }}
    </Formik>
  );
};
